import React, { useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  useDashboardAnalytics,
  useCostAnalytics,
  useConversationAnalytics,
} from "../../hooks/useAnalytics";
import {
  RefreshCw,
  Download,
  TrendingUp,
  Users,
  DollarSign,
  MessageCircle,
} from "lucide-react";

const COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Yellow
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#EC4899", // Pink
];

const Dashboard = () => {
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  const {
    data: dashboardData,
    isLoading: loadingDashboard,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useDashboardAnalytics();

  const { data: costData, isLoading: loadingCost } = useCostAnalytics();

  const { data: conversationData, isLoading: loadingConversation } =
    useConversationAnalytics();

  const isLoading = loadingDashboard || loadingCost || loadingConversation;

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return "$0.00";
    const numValue = Number(value);
    if (isNaN(numValue)) return "$0.00";
    return `$${numValue.toFixed(2)}`;
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null) return "0%";
    const numValue = Number(value);
    if (isNaN(numValue)) return "0%";
    return `${numValue.toFixed(1)}%`;
  };

  // Prepare media type data for pie chart
  const mediaTypeData = dashboardData?.media_stats
    ? [
        { name: "Images", value: dashboardData.media_stats.image_messages },
        { name: "Audio", value: dashboardData.media_stats.audio_messages },
        { name: "Video", value: dashboardData.media_stats.video_messages },
        {
          name: "Documents",
          value: dashboardData.media_stats.document_messages,
        },
      ].filter((item) => item.value > 0)
    : [];

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-lg text-gray-600 dark:text-gray-400">
              Loading analytics...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            Analytics Dashboard
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Real-time CRM performance insights
          </p>
        </div>
        <div className="flex gap-3">
          <div className="flex gap-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, start: e.target.value }))
              }
              className="border-gray-300 dark:border-gray-600 bg-transparent text-gray-700 dark:text-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500 dark:text-gray-400">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, end: e.target.value }))
              }
              className="border-gray-300 dark:border-gray-600 bg-transparent text-gray-700 dark:text-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => refetchDashboard()}
            className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg text-sm transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Total Chats
              </h4>
              <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                {dashboardData?.overview.total_chats?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                {dashboardData?.overview.escalated_chats || 0} escalated
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Automation Rate
              </h4>
              <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                {formatPercentage(dashboardData?.overview.automation_rate)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                AI efficiency
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Total Cost
              </h4>
              <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                {formatCurrency(dashboardData?.overview.total_cost)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formatCurrency(dashboardData?.overview.avg_cost_per_chat)} per
                chat
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-500 dark:text-gray-400 font-medium text-sm">
                Active Users
              </h4>
              <p className="text-3xl font-bold text-gray-800 dark:text-white mt-2">
                {conversationData?.overview.active_conversations || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {conversationData?.overview.resolved_conversations || 0}{" "}
                resolved
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend Chart */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-4">
            Daily Chat Volume & Cost
          </h4>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dashboardData?.daily_trend || []}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E2E8F0"
                className="dark:stroke-gray-600"
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("en", {
                    month: "short",
                    day: "numeric",
                  })
                }
                className="dark:fill-gray-300"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12 }}
                className="dark:fill-gray-300"
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                className="dark:fill-gray-300"
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "white",
                  color: "#1F2937",
                }}
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="total_chats"
                stroke="#3B82F6"
                strokeWidth={2}
                name="Total Chats"
                dot={{ r: 3 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="total_cost"
                stroke="#8B5CF6"
                strokeWidth={2}
                name="Total Cost ($)"
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Media Type Distribution */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-4">
            Media Type Distribution
          </h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={mediaTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {mediaTypeData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Provider */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-4">
            Cost by Provider
          </h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={costData?.by_provider || []}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E2E8F0"
                className="dark:stroke-gray-600"
              />
              <XAxis
                dataKey="provider"
                tick={{ fontSize: 12 }}
                className="dark:fill-gray-300"
              />
              <YAxis tick={{ fontSize: 12 }} className="dark:fill-gray-300" />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "white",
                  color: "#1F2937",
                }}
                formatter={(value) => [formatCurrency(value), "Cost"]}
              />
              <Bar dataKey="total_cost" fill="#3B82F6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Escalation Reasons */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-gray-800 dark:text-white mb-4">
            Top Escalation Reasons
          </h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={conversationData?.escalation_reasons || []}
              layout="horizontal"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E2E8F0"
                className="dark:stroke-gray-600"
              />
              <XAxis
                type="number"
                tick={{ fontSize: 12 }}
                className="dark:fill-gray-300"
              />
              <YAxis
                dataKey="escalation_reason"
                type="category"
                tick={{ fontSize: 12 }}
                width={120}
                className="dark:fill-gray-300"
              />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "white",
                  color: "#1F2937",
                }}
                formatter={(value) => [value, "Count"]}
              />
              <Bar dataKey="count" fill="#EF4444" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <h4 className="font-semibold text-gray-800 dark:text-white p-6 border-b border-gray-200 dark:border-gray-700">
            Performance Metrics
          </h4>
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                Average Resolution Time
              </span>
              <span className="font-semibold text-gray-800 dark:text-white">
                {conversationData?.resolution_times?.avg_resolution_time || "-"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                Median Resolution Time
              </span>
              <span className="font-semibold text-gray-800 dark:text-white">
                {conversationData?.resolution_times?.median_resolution_time ||
                  "-"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                90th Percentile
              </span>
              <span className="font-semibold text-gray-800 dark:text-white">
                {conversationData?.resolution_times?.percentile_90 || "-"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                Active Conversations
              </span>
              <span className="font-semibold text-gray-800 dark:text-white">
                {conversationData?.overview?.active_conversations || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">
                Resolved Today
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {conversationData?.overview?.resolved_conversations || 0}
              </span>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <h4 className="font-semibold text-gray-800 dark:text-white p-6 border-b border-gray-200 dark:border-gray-700">
            System Status
          </h4>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                API Connection
              </span>
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    dashboardError ? "bg-red-500" : "bg-green-500"
                  }`}
                ></div>
                <span
                  className={`text-sm font-medium ${
                    dashboardError
                      ? "text-red-600 dark:text-red-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {dashboardError ? "Disconnected (Using Mock)" : "Connected"}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Data Freshness
              </span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  Real-time
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Auto Refresh
              </span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  30s interval
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">
                Cache Status
              </span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  Optimized
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
